// @ts-strict-ignore
import { Container } from '@gassan-ui'
import { Article } from '@generated'
import { useAddArticleToCart } from '@lib/hooks/use-add-article-to-cart'
import { useCart } from '@lib/hooks/useCart'
import { getKontentImage } from '@lib/kontent'
import { useTranslation } from 'next-i18next'
import { useState, useEffect } from 'react'
import CtaButtons from '../CtaButtons'
import Indicators from '../Sliders/Indicators'
import ProductSlider from '../Sliders/ProductSlider'
import Thumbnails from '../Sliders/ProductSlider/Thumbnails'
import { Button } from '../ui/Button'
import { Heading } from '../ui/Heading'
import { BasicInfo } from './BasicInfo'
import dynamic from 'next/dynamic'
import { preloadImage } from '@lib/preload-image'
import { imgix } from '@lib/imigx'

const Product360View = dynamic(() => import('../Product360View'))

type Props = {
  data: Article
}

const ProductInfo = ({ data }: Props) => {
  const [index, setIndex] = useState(0)
  const [is360ViewOpen, setIs360ViewOpen] = useState(false)
  const { handleAddArticleToCart } = useAddArticleToCart()
  const cart = useCart()
  const { t } = useTranslation(['other', 'rolex'])
  const hasItemInCart = cart?.items.some((item) => item.articleId === data.id)

  const { id, modelName } = data

  const imagesSlider = data.images.filter((url) => !url.includes('_360'))
  const images360 = data.images
    .filter((url) => url.includes('_360'))
    .sort((a, b) => {
      // Updated regex to handle both url formats: _360_4.png and _360_(4).png
      const getIndex = (url: string) => {
        const match = url.match(/_360_\(?(\d+)\)?\.png$/)
        return parseInt(match?.[1] || '0', 10)
      }
      return getIndex(b) - getIndex(a)
    })
    .map((url) => imgix(url, { width: 850 }))
  const has360Images = images360.length > 0

  // Preload the first 360 image
  useEffect(() => {
    if (has360Images) {
      // Returns a promise, but we don't need to wait for it to resolve
      preloadImage(images360[0])
    }
  }, [has360Images, images360])

  return (
    <Container className="mx-auto text-rolexShade-900 lg:text-white">
      <div className="grid grid-cols-1 gap-1 lg:flex">
        <div className="mb-5 block pt-4 text-center sm:mb-8 lg:hidden">
          <BasicInfo data={data} />
        </div>
        <div className="block w-full items-center justify-center bg-black lg:flex">
          <ProductSlider
            activeIndex={index}
            onChange={setIndex}
            sources={imagesSlider.map((url) => getKontentImage(url, { width: 750, height: 750 }))}
            title={`Rolex ${modelName}`}
            onSet360View={has360Images ? () => setIs360ViewOpen(true) : undefined}
          />
          <div className="mt-5 block lg:hidden">
            <Indicators
              className="mb-0"
              activeIndex={index}
              onClick={(goToIndex) => {
                setIndex(goToIndex)
              }}
              count={imagesSlider.length}
            />
          </div>
        </div>
        <div className="flex items-center justify-center py-16 lg:justify-start lg:bg-black lg:pl-[10%] lg:pr-[10%]">
          <div className="w-full overflow-hidden">
            <div className="hidden lg:block">
              <h1>
                <BasicInfo data={data} />
              </h1>
            </div>
            <div className="flex w-full justify-center lg:mt-4 lg:block">
              <Button
                variant="primary"
                disabled={hasItemInCart}
                onClick={() => handleAddArticleToCart({ articleId: id })}
              >
                {hasItemInCart
                  ? t('addedToCart', { ns: 'other' })
                  : t('addToCartAlt', { ns: 'other' })}
              </Button>
            </div>
            <div className="mb-8 mt-5">
              <Heading
                as="h2"
                variant="very-small-title"
                className="mb-5 block text-center text-current lg:hidden"
              >
                {t('detail.contactUs', { ns: 'rolex' })}
              </Heading>
              <CtaButtons phoneNumber="+31 (0)20 622 5333" findStore sendMessage />
            </div>
            <div className="hidden lg:contents">
              <Thumbnails
                activeIndex={index}
                setIndex={setIndex}
                sources={imagesSlider.map((url) =>
                  getKontentImage(url, { width: 100, height: 100 }),
                )}
              />
            </div>
          </div>
        </div>
      </div>
      {has360Images && (
        <Product360View
          images={images360}
          imagesSlider={imagesSlider}
          title={`Rolex ${modelName} Modal`}
          isOpen={is360ViewOpen}
          onClose={() => setIs360ViewOpen(false)}
        />
      )}
    </Container>
  )
}

export default ProductInfo
